import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import { commandMenuKeywords } from '@/constant/command-menu-keywords'
import { adminMenu, orgMenu } from '@/constant/sidebar'
import type { Feature, Role } from '@/enum/global.enum'
import { useActiveOrg } from '@/hooks/use-active-org'
import { usePermissions } from '@/hooks/use-permissions'
import type { IUserOrganisations } from '@/interface/auth.interface'
import type { IClerkUser } from '@/interface/clerk.interface'
import { isAdminRole, isOrgRole } from '@/utils/role-check.utils'
import { buildSearchKeywords, commandMenuFilter, highlightMatches } from '@/utils/command-menu-search.utils'
import { Kbd } from '@/components/ui/kbd'
import { useTheme } from '@/hooks/use-theme'
import { ArrowLeft, ChevronRight, Clock, Link2, Moon, RotateCw, Sun } from 'lucide-react'
import type { ForwardRefExoticComponent, ReactNode, RefAttributes, SVGProps } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { toast } from 'sonner'

// Define the type for flattened menu items
interface FlattenedMenuItem {
  title: string
  url: string
  icon?: ForwardRefExoticComponent<Omit<SVGProps<SVGSVGElement>, 'ref'> & RefAttributes<SVGSVGElement>>
  role?: string[]
  feature?: string
  parentTitle?: string
  keywords: string[]
}

// Searchable fields for a page: its title, configured aliases and parent sections (e.g. "Governance")
const getKeywords = (title: string, url: string, parentTitle?: string): string[] =>
  buildSearchKeywords({
    title,
    aliases: commandMenuKeywords[url],
    sections: parentTitle ? parentTitle.split(' > ').map((section) => section.trim()) : [],
  })

const RECENT_PAGES_KEY = 'command-menu-recent'
const MAX_RECENT_PAGES = 5

const readRecentPages = (): string[] => {
  try {
    const stored = JSON.parse(localStorage.getItem(RECENT_PAGES_KEY) ?? '[]')
    return Array.isArray(stored) ? stored.filter((url) => typeof url === 'string') : []
  } catch {
    return []
  }
}

const saveRecentPage = (url: string): string[] => {
  const recent = [url, ...readRecentPages().filter((recentUrl) => recentUrl !== url)].slice(0, MAX_RECENT_PAGES)
  try {
    localStorage.setItem(RECENT_PAGES_KEY, JSON.stringify(recent))
  } catch {
    // Storage unavailable (private mode) - recent pages just won't persist
  }
  return recent
}

const HighlightedTitle = ({ title, search }: { title: string; search: string }) => (
  <>
    {highlightMatches(title, search).map((part, index) =>
      part.match ? (
        <mark key={index} className='bg-transparent font-semibold text-primary'>
          {part.text}
        </mark>
      ) : (
        <span key={index}>{part.text}</span>
      )
    )}
  </>
)

// Define types for the sidebar menu structure
interface MenuItem {
  title?: string
  name?: string
  url?: string
  icon?: ForwardRefExoticComponent<Omit<SVGProps<SVGSVGElement>, 'ref'> & RefAttributes<SVGSVGElement>>
  role?: string[]
  feature?: string
  items?: MenuItem[]
}

interface MenuSection {
  title: string
  hideCommandMenu?: boolean
  url?: string
  icon?: ForwardRefExoticComponent<Omit<SVGProps<SVGSVGElement>, 'ref'> & RefAttributes<SVGSVGElement>>
  role?: string[]
  feature?: string
  isActive?: boolean
  items?: MenuItem[]
}

interface MenuData {
  navMain?: MenuSection[]
  others?: MenuItem[]
}

export function CommandMenu({
  user,
  organisations,
}: {
  user: IClerkUser | null | undefined
  organisations: IUserOrganisations['organisations'] | null
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const { activeOrg } = useActiveOrg()
  const { theme, setTheme } = useTheme()
  const { canNavigate } = usePermissions()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [recentUrls, setRecentUrls] = useState<string[]>(readRecentPages)
  const [menu, setMenu] = useState<FlattenedMenuItem[]>([])
  const [settings, setSettings] = useState<FlattenedMenuItem[]>([])
  const [others, setOthers] = useState<FlattenedMenuItem[]>([])

  // Recursive function to process menu items and their nested items
  const processMenuItem = (
    item: MenuItem,
    parentTitle: string,
    isSettings: boolean,
    menuItems: FlattenedMenuItem[],
    settingsItems: FlattenedMenuItem[]
  ): void => {
    const itemTitle = item.title || item.name || 'Untitled'
    const itemUrl = item.url || '#'

    // If the item has nested items, process them recursively
    if (item.items && Array.isArray(item.items) && item.items.length > 0) {
      // Process nested items with updated parent title
      const newParentTitle = parentTitle ? `${parentTitle} > ${itemTitle}` : itemTitle
      item.items.forEach((nestedItem) => {
        processMenuItem(nestedItem, newParentTitle, isSettings, menuItems, settingsItems)
      })
    }

    // Only add items with valid URLs (not '#') to the flattened menu
    // Skip items with title "Dashboard"
    if (itemUrl !== '#' && itemTitle !== 'Dashboard') {
      const flattenedItem: FlattenedMenuItem = {
        title: itemTitle,
        url: itemUrl,
        icon: item.icon,
        role: item.role,
        feature: item.feature,
        parentTitle: parentTitle || undefined,
        keywords: getKeywords(itemTitle, itemUrl, parentTitle),
      }

      if (isSettings) settingsItems.push(flattenedItem)
      else menuItems.push(flattenedItem)
    }
  }

  // Function to flatten menu items recursively
  const flattenMenuItems = (
    menuData: MenuData
  ): { menu: FlattenedMenuItem[]; settings: FlattenedMenuItem[]; others: FlattenedMenuItem[] } => {
    const menuItems: FlattenedMenuItem[] = []
    const settingsItems: FlattenedMenuItem[] = []
    const otherItems: FlattenedMenuItem[] = []

    if (menuData?.navMain) {
      menuData.navMain.forEach((section: MenuSection) => {
        const isSettings = section.title === 'Settings'
        const sectionUrl = section.url || '#'

        if (section?.hideCommandMenu === true) return

        // If section has a valid URL, add it to the appropriate array
        // Skip sections with title "Dashboard"
        if (sectionUrl !== '#' && section.title !== 'Dashboard') {
          const sectionItem: FlattenedMenuItem = {
            title: section.title,
            url: sectionUrl,
            icon: section.icon,
            role: section.role,
            feature: section.feature,
            keywords: getKeywords(section.title, sectionUrl),
          }

          if (isSettings) settingsItems.push(sectionItem)
          else menuItems.push(sectionItem)
        }

        // Process section items recursively
        if (section.items && Array.isArray(section.items))
          section.items.forEach((item: MenuItem) => {
            processMenuItem(item, section.title, isSettings, menuItems, settingsItems)
          })
      })
    }

    // Flatten the `others` menu (including nested groups like Logs) so those entries stay searchable
    if (menuData?.others && Array.isArray(menuData.others)) {
      menuData.others.forEach((item: MenuItem) => {
        processMenuItem(item, '', false, otherItems, settingsItems)
      })
    }

    return { menu: menuItems, settings: settingsItems, others: otherItems }
  }

  useEffect(() => {
    if (activeOrg?.role && isAdminRole(activeOrg.role)) {
      const { menu: adminMenuItems, settings: adminSettings, others: adminOthers } = flattenMenuItems(adminMenu)
      setMenu(adminMenuItems)
      setSettings(adminSettings)
      setOthers(adminOthers)
    } else if (activeOrg?.role && isOrgRole(activeOrg.role)) {
      const { menu: orgMenuItems, settings: orgSettings, others: orgOthers } = flattenMenuItems(orgMenu)
      setMenu(orgMenuItems)
      setSettings(orgSettings)
      setOthers(orgOthers)
    }
  }, [activeOrg, user, organisations])

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen((open) => !open)
      }
    }
    document.addEventListener('keydown', down)
    return () => document.removeEventListener('keydown', down)
  }, [])

  // Start every session with a blank search
  useEffect(() => {
    if (!open) setSearch('')
  }, [open])

  const allowedItems = useMemo(
    () =>
      [...menu, ...settings, ...others].filter((item) => canNavigate(item?.role as Role[], item?.feature as Feature)),
    [menu, settings, others, canNavigate]
  )

  const recentItems = useMemo(
    () =>
      recentUrls
        .map((url) => allowedItems.find((item) => item.url === url))
        .filter((item): item is FlattenedMenuItem => Boolean(item) && item?.url !== location.pathname),
    [recentUrls, allowedItems, location.pathname]
  )

  const handleSelect = (url: string) => {
    setOpen(false)
    if (url === '#') return
    setRecentUrls(saveRecentPage(url))
    navigate(url)
  }

  const runAction = (action: () => void) => {
    setOpen(false)
    action()
  }

  const actions: { id: string; title: string; keywords: string[]; icon: ReactNode; run: () => void }[] = [
    {
      id: 'toggle-theme',
      title: theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme',
      keywords: ['theme', 'dark mode', 'light mode', 'appearance', 'night mode'],
      icon: theme === 'dark' ? <Sun /> : <Moon />,
      run: () => setTheme(theme === 'dark' ? 'light' : 'dark'),
    },
    {
      id: 'copy-link',
      title: 'Copy link to this page',
      keywords: ['copy url', 'share page', 'copy link'],
      icon: <Link2 />,
      run: () =>
        navigator.clipboard
          .writeText(window.location.href)
          .then(() => toast.success('Page link copied'))
          .catch(() => toast.error('Could not copy the link')),
    },
    {
      id: 'go-back',
      title: 'Go back',
      keywords: ['previous page', 'back', 'return'],
      icon: <ArrowLeft />,
      run: () => navigate(-1),
    },
    {
      id: 'reload',
      title: 'Reload page',
      keywords: ['refresh', 'reload'],
      icon: <RotateCw />,
      run: () => window.location.reload(),
    },
  ]

  const renderItems = (items: FlattenedMenuItem[], group: string) =>
    items.map((item) => {
      const isAllowed = canNavigate(item?.role as Role[], item?.feature as Feature)
      if (!isAllowed) return null
      return (
        <CommandItem
          key={`${group}-${item.url}-${item.title}`}
          // Group + URL + title keeps items unique (the same page can appear under Recent too)
          value={`${group}:${item.url}:${item.title}`}
          keywords={item.keywords}
          className='flex gap-2'
          onSelect={() => handleSelect(item.url ?? '#')}
        >
          {group === 'recent' ? <Clock className='mt-[1px]' /> : item.icon && <item.icon className='mt-[1px]' />}
          <div className='flex min-w-0 flex-1 items-center gap-2'>
            <span className='truncate'>
              <HighlightedTitle title={item.title} search={search} />
            </span>
            {item.parentTitle && (
              <span className='text-muted-foreground ml-auto hidden shrink-0 items-center gap-1 text-xs sm:flex'>
                {item.parentTitle.split(' > ').map((section, index) => (
                  <span key={section} className='flex items-center gap-1'>
                    {index > 0 && <ChevronRight className='size-3!' />}
                    {section}
                  </span>
                ))}
              </span>
            )}
          </div>
        </CommandItem>
      )
    })

  return (
    <CommandDialog open={open} onOpenChange={setOpen} filter={commandMenuFilter}>
      <CommandInput placeholder='Search pages, settings and actions...' value={search} onValueChange={setSearch} />
      <CommandList>
        <CommandEmpty>
          <div className='flex flex-col items-center gap-1 px-4'>
            <span>
              No results for <span className='font-medium'>&ldquo;{search}&rdquo;</span>
            </span>
            <span className='text-muted-foreground text-xs'>
              Try a page name, an abbreviation (e.g. &ldquo;poam&rdquo;, &ldquo;ncr&rdquo;) or fewer words.
            </span>
          </div>
        </CommandEmpty>
        {!search.trim() && recentItems.length > 0 && (
          <>
            <CommandGroup heading='Recent'>{renderItems(recentItems, 'recent')}</CommandGroup>
            <CommandSeparator />
          </>
        )}
        <CommandGroup heading='Pages'>{renderItems(menu, 'menu')}</CommandGroup>
        {settings?.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading='Settings'>{renderItems(settings, 'settings')}</CommandGroup>
          </>
        )}
        {others?.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading='Others'>{renderItems(others, 'others')}</CommandGroup>
          </>
        )}
        <CommandSeparator />
        <CommandGroup heading='Actions'>
          {actions.map((action) => (
            <CommandItem
              key={action.id}
              value={`action:${action.id}`}
              keywords={buildSearchKeywords({ title: action.title, aliases: action.keywords })}
              onSelect={() => runAction(action.run)}
            >
              {action.icon}
              <span>
                <HighlightedTitle title={action.title} search={search} />
              </span>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
      <div className='text-muted-foreground flex items-center gap-4 border-t px-3 py-2 text-xs'>
        <span className='flex items-center gap-1'>
          <Kbd>↑</Kbd>
          <Kbd>↓</Kbd> to navigate
        </span>
        <span className='flex items-center gap-1'>
          <Kbd>↵</Kbd> to open
        </span>
        <span className='flex items-center gap-1'>
          <Kbd>Esc</Kbd> to close
        </span>
      </div>
    </CommandDialog>
  )
}
